import { usePostHogCapture } from '@app/core/integrations/posthog/usePostHogCapture';
import { useUiStore } from '@app/core/ui/stores/ui.store';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import debugFn from 'debug';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { config } from '../../config';
import {
  getCurrentIndexScriptHash,
  parseIndexScriptHashFromHtml,
} from './index-hash/index-script-hash';

const debug = debugFn('useVersionCheck');
const COOLDOWN_MS = 24 * 60 * 60 * 1000;

export type VersionReloadSource = 'alert' | 'sidebar';

export const useVersionCheck = () => {
  const checkInterval = useFeatureValue('app__version_check_interval_mins', 1) * 60000;
  const capture = usePostHogCapture();

  const [isNewVersionAvailable, setAvailable] = useState(false);

  const versionCheckLastAlertShownAt = useUiStore((s) => s.versionCheckLastAlertShownAt);
  const markVersionAlertShown = useUiStore((s) => s.markVersionAlertShown);

  const currentHash = useMemo(() => getCurrentIndexScriptHash(), []);

  const { data: html } = useQuery({
    queryKey: ['version-check', currentHash],
    queryFn: () =>
      axios.get<string>(window.location.origin + '/', { responseType: 'text' }).then((r) => r.data),
    enabled: !!checkInterval && !config.isNodeDev && !!currentHash,
    refetchInterval: checkInterval,
    staleTime: checkInterval,
  });

  useEffect(() => {
    if (!html || !currentHash) {
      return;
    }

    const fetchedHash = parseIndexScriptHashFromHtml(html);
    if (!!fetchedHash && fetchedHash !== currentHash) {
      debug('new version available: %s -> %s', currentHash, fetchedHash);
      setAvailable(true);
    }
  }, [html, currentHash]);

  const dismiss = () => {
    setAvailable(false);
  };

  const lastShownAt = versionCheckLastAlertShownAt ?? undefined;
  const shouldShowFullAlert =
    isNewVersionAvailable && (lastShownAt === undefined || Date.now() - lastShownAt > COOLDOWN_MS);

  const reload = useCallback(
    (source: VersionReloadSource) => {
      markVersionAlertShown();
      capture('new_version_reload', { type: source });
      window.location.reload();
    },
    [markVersionAlertShown, capture],
  );

  return {
    isNewVersionAvailable,
    shouldShowFullAlert,
    dismiss,
    markAlertShown: markVersionAlertShown,
    reload,
  };
};
